import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox as InboxIcon, Send, ArrowLeft, MessageSquare, ShieldCheck, Bell, BellOff, Award, GraduationCap, AtSign, PartyPopper, ExternalLink, Loader2 } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import Footer from "../components/Footer";

// Format ISO timestamp into a compact relative-or-date string.
const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

// Inline "Celebrate on X" CTA shown inside milestone inbox threads.
// Same backend as the dashboard banner — POST /x/share/self with a
// `milestone_threshold` arg → server returns the pre-built celebration
// intent URL + grants +1 credit. Hides itself after a successful share
// or on a 429 (cap hit) so the thread stays tidy.
const InboxMilestoneCTA = ({ videoId, threshold, subject }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  if (!threshold) return null;
  if (done) {
    return (
      <div
        data-testid="inbox-milestone-celebrated"
        className="mt-3 inline-flex items-center gap-1.5 text-emerald-300 text-xs"
      >
        <PartyPopper className="h-3.5 w-3.5" />
        Celebration tweet ready in a new tab — +1 credit earned.
      </div>
    );
  }

  const celebrate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/x/share/self", {
        video_id: videoId,
        milestone_threshold: threshold,
      });
      const { intent_url, reward_credits, remaining_today } = r.data || {};
      if (intent_url) window.open(intent_url, "_blank", "noopener,noreferrer");
      toast.success(`+${reward_credits} credit — celebrate on X! (${remaining_today} shares left today)`);
      setDone(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 429) {
        toast.info(typeof detail === "string" ? detail : "Daily share cap reached.");
        setDone(true);
      } else {
        toast.error(typeof detail === "string" ? detail : "Couldn't open the celebration tweet.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="inbox-milestone-cta"
      className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 rounded-lg border border-sky-700/40 bg-sky-950/30 p-3"
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <PartyPopper className="h-4 w-4 text-amber-300 shrink-0" />
        <p className="text-sky-100 text-sm">
          One-tap brag on X — earn{" "}
          <span className="text-amber-300 font-semibold">+1 credit</span>
          {subject ? <span className="text-gray-400 hidden sm:inline"> · {subject}</span> : null}
        </p>
      </div>
      <Button
        onClick={celebrate}
        disabled={busy}
        size="sm"
        data-testid="inbox-milestone-celebrate-btn"
        className="bg-sky-600 hover:bg-sky-700 text-white shrink-0"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
        Celebrate on X
        <ExternalLink className="h-3 w-3 ml-1.5 opacity-70" />
      </Button>
    </div>
  );
};

const ThreadList = ({ threads, activeId }) => {
  if (!threads.length) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 text-center" data-testid="inbox-empty">
        <MessageSquare className="h-8 w-8 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">No conversations yet.</p>
        <Link to="/contact" className="text-emerald-400 hover:text-emerald-300 text-sm mt-2 inline-block">
          Start one →
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="inbox-thread-list">
      {threads.map((t) => {
        const unread = (t.unread_for_user || 0) > 0;
        const isActive = t.id === activeId;
        return (
          <Link
            key={t.id}
            to={`/inbox/${t.id}`}
            className={`block rounded-lg p-3 border transition-colors ${
              isActive
                ? "bg-emerald-600/15 border-emerald-500/50"
                : unread
                ? "bg-gray-900 border-emerald-500/30 hover:border-emerald-500/60"
                : "bg-gray-900 border-gray-800 hover:border-gray-700"
            }`}
            data-testid={`inbox-thread-${t.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className={`truncate text-sm ${unread ? "text-white font-semibold" : "text-gray-300"}`}>
                  {t.subject || "(no subject)"}
                </p>
                <p className="text-gray-500 text-[11px] mt-0.5">
                  {t.status === "closed" ? "Closed · " : ""}
                  Last update {formatStamp(t.last_message_at)}
                </p>
              </div>
              {unread && (
                <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-emerald-500 text-black text-[10px] font-bold flex items-center justify-center">
                  {t.unread_for_user}
                </span>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
};

const ThreadDetail = ({ threadId, onChanged }) => {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/me/messages/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load thread");
      setThread(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/me/messages/${threadId}/reply`, { message: body.trim() });
      setBody("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send reply");
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }
  if (!thread) {
    return <p className="text-gray-500 text-sm">Thread not found.</p>;
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg flex flex-col" data-testid="thread-detail" style={{ minHeight: "60vh" }}>
      <div className="border-b border-gray-800 p-4">
        <Link to="/inbox" className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1 md:hidden mb-2">
          <ArrowLeft className="h-3 w-3" /> Back to inbox
        </Link>
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-white font-semibold text-base sm:text-lg truncate" data-testid="thread-subject">
            {thread.subject || "(no subject)"}
          </h2>
          {thread.status === "closed" && (
            <span className="text-[10px] uppercase tracking-wider bg-gray-800 text-gray-400 px-2 py-0.5 rounded">closed</span>
          )}
        </div>
        {thread.kind === "milestone" && thread.video_id && (
          <InboxMilestoneCTA
            videoId={thread.video_id}
            threshold={thread.milestone_threshold}
            subject={thread.subject}
          />
        )}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="thread-messages">
        {messages.map((m) => {
          const fromAdmin = m.sender_role === "admin";
          return (
            <div
              key={m.id}
              className={`flex ${fromAdmin ? "justify-start" : "justify-end"}`}
              data-testid={`msg-${m.id}`}
            >
              <div
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  fromAdmin
                    ? "bg-gray-800 text-gray-200 border border-gray-700"
                    : "bg-emerald-600/20 text-emerald-50 border border-emerald-600/40"
                }`}
              >
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 opacity-70">
                  {fromAdmin && <ShieldCheck className="h-3 w-3 text-amber-400" />}
                  <span>{fromAdmin ? "GlobalEyes team" : "you"}</span>
                  <span>·</span>
                  <span>{formatStamp(m.created_at)}</span>
                </div>
                {m.body}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={sendReply} className="border-t border-gray-800 p-3 flex flex-col gap-2" data-testid="thread-reply-form">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={thread.status === "closed" ? "Write a follow-up to re-open this thread…" : "Type your reply…"}
          rows={3}
          className="bg-gray-950 border-gray-700 text-white resize-none"
          data-testid="thread-reply-input"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={busy || !body.trim()} className="bg-emerald-600 hover:bg-emerald-700" data-testid="thread-reply-submit">
            <Send className="h-4 w-4 mr-1.5" />
            {busy ? "Sending…" : "Send reply"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const MentionsList = ({ mentions }) => {
  if (!mentions || mentions.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-lg p-10 text-center" data-testid="mentions-empty">
        <AtSign className="h-8 w-8 text-gray-600 mx-auto mb-3" />
        <p className="text-gray-400 text-sm">
          No one has @-mentioned you in a comment yet.
        </p>
        <p className="text-gray-600 text-xs mt-1">
          When someone writes <span className="text-emerald-400">@{`{your username}`}</span> on any video, it'll show up here.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="mentions-list">
      {mentions.map((m) => (
        <Link
          key={m.id}
          to={`/video/${m.video_id}#comment-${m.comment_id}`}
          className={`block rounded-lg p-3 border transition-colors ${
            m.read
              ? "bg-gray-900 border-gray-800 hover:border-gray-700"
              : "bg-gray-900 border-emerald-500/30 hover:border-emerald-500/60"
          }`}
          data-testid={`mention-row-${m.id}`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm">
                <span className="text-emerald-400 font-semibold">@{m.author_username}</span>
                <span className="text-gray-500"> mentioned you on </span>
                <span className="text-white">"{m.video_title || "a video"}"</span>
              </p>
              <p className="text-gray-400 text-xs mt-1 line-clamp-2">{m.snippet}</p>
              <p className="text-gray-600 text-[11px] mt-1">{formatStamp(m.created_at)}</p>
            </div>
            {!m.read && (
              <span className="shrink-0 mt-1 h-2 w-2 rounded-full bg-emerald-500" />
            )}
          </div>
        </Link>
      ))}
    </div>
  );
};

const InboxPage = () => {
  const { user } = useAuth();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(true);
  const [notifyMilestones, setNotifyMilestones] = useState(true);
  const [notifyOnboarding, setNotifyOnboarding] = useState(true);
  const [savingPref, setSavingPref] = useState(false);
  // Tab state — "messages" shows admin↔user threads, "mentions" shows
  // who's @-mentioned the current user in a comment somewhere.
  const [tab, setTab] = useState("messages");
  const [mentions, setMentions] = useState([]);
  const [mentionsUnread, setMentionsUnread] = useState(0);

  const loadThreads = async () => {
    try {
      const { data } = await api.get("/me/messages");
      setThreads(data.items);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load inbox");
    }
    setLoading(false);
  };

  const loadPrefs = async () => {
    try {
      const { data } = await api.get("/me/prefs");
      setNotifyEmail(data.notify_inbox_email !== false);
      setNotifyMilestones(data.notify_milestones_email !== false);
      setNotifyOnboarding(data.notify_onboarding_email !== false);
    } catch (err) { /* silent — defaults are sane */ }
  };

  const loadMentions = async ({ markRead = false } = {}) => {
    try {
      const { data } = await api.get("/me/mentions?limit=50");
      setMentions(data.items || []);
      const unread = (data.items || []).filter((m) => !m.read).length;
      setMentionsUnread(unread);
      if (markRead && unread > 0) {
        try {
          await api.post("/me/mentions/read-all");
          setMentionsUnread(0);
          setMentions((prev) => prev.map((m) => ({ ...m, read: true })));
          window.dispatchEvent(new Event("inbox-read"));
        } catch (_) { /* non-fatal */ }
      }
    } catch (err) { /* silent — feature is opt-in */ }
  };

  const toggleNotify = async () => {
    const next = !notifyEmail;
    setSavingPref(true);
    setNotifyEmail(next);  // optimistic
    try {
      await api.patch("/me/prefs", { notify_inbox_email: next });
      toast.success(next ? "Reply notifications on" : "Reply notifications off");
    } catch (err) {
      setNotifyEmail(!next);  // revert
      toast.error(err.response?.data?.detail || "Couldn't update preference");
    }
    setSavingPref(false);
  };

  const toggleMilestones = async () => {
    const next = !notifyMilestones;
    setSavingPref(true);
    setNotifyMilestones(next);
    try {
      await api.patch("/me/prefs", { notify_milestones_email: next });
      toast.success(next ? "Milestone emails on" : "Milestone emails muted");
    } catch (err) {
      setNotifyMilestones(!next);
      toast.error(err.response?.data?.detail || "Couldn't update preference");
    }
    setSavingPref(false);
  };

  const toggleOnboarding = async () => {
    const next = !notifyOnboarding;
    setSavingPref(true);
    setNotifyOnboarding(next);
    try {
      await api.patch("/me/prefs", { notify_onboarding_email: next });
      toast.success(next ? "Onboarding tips on" : "Onboarding tips muted");
    } catch (err) {
      setNotifyOnboarding(!next);
      toast.error(err.response?.data?.detail || "Couldn't update preference");
    }
    setSavingPref(false);
  };

  useEffect(() => {
    if (!user) return;
    loadThreads();
    loadPrefs();
    loadMentions();
    // Tell the Header to zero its inbox badge — opening the inbox itself
    // is the "I saw it" signal. The per-thread GET will clear DB-side too.
    window.dispatchEvent(new Event("inbox-read"));
    const onFocus = () => { loadThreads(); loadMentions(); };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // When the user clicks the Mentions tab, fetch fresh + mark all as read.
  useEffect(() => {
    if (!user || tab !== "mentions") return;
    loadMentions({ markRead: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user]);

  if (!user) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200 flex flex-col" data-testid="inbox-page">
      <div className="container mx-auto px-4 py-8 max-w-5xl flex-1 w-full">
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <InboxIcon className="h-6 w-6 text-emerald-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Inbox</h1>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
            <button
              onClick={toggleNotify}
              disabled={savingPref}
              title={notifyEmail
                ? "We'll email you when staff replies — click to turn off"
                : "Reply email notifications are off — click to turn back on"}
              className={`flex items-center gap-1.5 text-xs px-2 sm:px-3 py-1.5 rounded-full border transition-colors ${
                notifyEmail
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                  : "border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600"
              }`}
              data-testid="inbox-notify-toggle"
              data-notify-enabled={notifyEmail ? "true" : "false"}
            >
              {notifyEmail ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
              <span className="hidden md:inline">{notifyEmail ? "Email on reply" : "Reply emails muted"}</span>
              <span className="md:hidden">Email</span>
            </button>
            <button
              onClick={toggleMilestones}
              disabled={savingPref}
              title={notifyMilestones
                ? "We'll email you when your video hits 10/100/1k/10k loops — click to turn off"
                : "Milestone emails are off — click to turn back on"}
              className={`flex items-center gap-1.5 text-xs px-2 sm:px-3 py-1.5 rounded-full border transition-colors ${
                notifyMilestones
                  ? "border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20"
                  : "border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600"
              }`}
              data-testid="inbox-milestone-toggle"
              data-notify-enabled={notifyMilestones ? "true" : "false"}
            >
              <Award className="h-3.5 w-3.5" />
              <span className="hidden md:inline">{notifyMilestones ? "Milestone alerts on" : "Milestones muted"}</span>
              <span className="md:hidden">Milestones</span>
            </button>
            <button
              onClick={toggleOnboarding}
              disabled={savingPref}
              title={notifyOnboarding
                ? "We'll email you onboarding tips during your first week — click to turn off"
                : "Onboarding tips are off — click to turn back on"}
              className={`flex items-center gap-1.5 text-xs px-2 sm:px-3 py-1.5 rounded-full border transition-colors ${
                notifyOnboarding
                  ? "border-violet-500/40 bg-violet-500/10 text-violet-300 hover:bg-violet-500/20"
                  : "border-gray-700 bg-gray-900 text-gray-400 hover:border-gray-600"
              }`}
              data-testid="inbox-onboarding-toggle"
              data-notify-enabled={notifyOnboarding ? "true" : "false"}
            >
              <GraduationCap className="h-3.5 w-3.5" />
              <span className="hidden md:inline">{notifyOnboarding ? "Onboarding tips on" : "Tips muted"}</span>
              <span className="md:hidden">Tips</span>
            </button>
            <Button onClick={() => navigate("/contact")} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-xs h-auto py-1.5 px-3" data-testid="inbox-new-thread-btn">
              New message
            </Button>
          </div>
        </div>

        {/* Tab switcher: Messages ↔ Mentions */}
        <div className="flex items-center gap-1 mb-4 border-b border-gray-800" data-testid="inbox-tabs">
          <button
            onClick={() => setTab("messages")}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === "messages"
                ? "text-emerald-400 border-emerald-400"
                : "text-gray-400 border-transparent hover:text-gray-200"
            }`}
            data-testid="inbox-tab-messages"
          >
            <MessageSquare className="inline h-4 w-4 mr-1.5 -mt-0.5" />
            Messages
          </button>
          <button
            onClick={() => setTab("mentions")}
            className={`relative px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              tab === "mentions"
                ? "text-emerald-400 border-emerald-400"
                : "text-gray-400 border-transparent hover:text-gray-200"
            }`}
            data-testid="inbox-tab-mentions"
          >
            <AtSign className="inline h-4 w-4 mr-1.5 -mt-0.5" />
            Mentions
            {mentionsUnread > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-500 text-black text-[10px] font-bold align-middle"
                data-testid="inbox-mentions-badge"
              >
                {mentionsUnread}
              </span>
            )}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" />
          </div>
        ) : tab === "mentions" ? (
          <MentionsList mentions={mentions} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`${threadId ? "hidden md:block" : ""} md:col-span-1`}>
              <ThreadList threads={threads} activeId={threadId} />
            </div>
            <div className={`${threadId ? "" : "hidden md:block"} md:col-span-2`}>
              {threadId ? (
                <ThreadDetail threadId={threadId} onChanged={loadThreads} />
              ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center text-gray-500 text-sm h-full flex flex-col items-center justify-center" style={{ minHeight: "40vh" }}>
                  <MessageSquare className="h-8 w-8 text-gray-700 mb-3" />
                  Pick a conversation on the left, or open a new one.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default InboxPage;
